import React, { useState, useEffect, useRef } from 'react';
import { Send, Hash, Search, Smile, MoreVertical, Reply, Pin, X, MessageSquare, AtSign, Menu, ChevronLeft, Users } from 'lucide-react';
import AccessGate from './AccessGate';

const CommunityPage = ({ user }) => {
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [accessLevel, setAccessLevel] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [threadMessages, setThreadMessages] = useState({});
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const messagesEndRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const inputRef = useRef(null);
  const sidebarRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    checkAccessLevel();
    fetchChannels();
    fetchUserRole();
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSidebar && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setShowSidebar(false);
      }
    };
    if (showSidebar) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSidebar]);

  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const checkAccessLevel = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/access/level`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAccessLevel(data);
      }
    } catch (error) {
      console.error('Error checking access level:', error);
    }
  };

  useEffect(() => {
    if (activeChannel) {
      fetchMessages(activeChannel.id);
      fetchPinnedMessages(activeChannel.id);
    }
  }, [activeChannel]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const fetchChannels = async () => {
    try {
      setErrorMessage(null);
      const response = await fetch(`${backendUrl}/api/community/channels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Channels loaded:', data.length);
        setChannels(data);
        if (data.length > 0) {
          setActiveChannel(data[0]);
        } else {
          setErrorMessage('No channels found. Please contact admin to set up community channels.');
        }
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to load channels:', response.status, errorData);
        setErrorMessage(`Error ${response.status}: ${errorData.detail || 'Failed to load community'}`);
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
      setErrorMessage(`Network error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (channelId) => {
    try {
      const response = await fetch(`${backendUrl}/api/community/channels/${channelId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchPinnedMessages = async (channelId) => {
    try {
      const response = await fetch(`${backendUrl}/api/community/channels/${channelId}/pinned`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPinnedMessages(data);
      }
    } catch (error) {
      console.error('Error fetching pinned messages:', error);
    }
  };

  const fetchThreadReplies = async (messageId) => {
    try {
      const response = await fetch(`${backendUrl}/api/community/messages/${messageId}/replies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setThreadMessages(prev => ({ ...prev, [messageId]: data }));
      }
    } catch (error) {
      console.error('Error fetching replies:', error);
    }
  };

  const handleMentionSearch = async (query) => {
    if (query.length < 1) {
      setShowMentionSuggestions(false);
      return;
    }
    try {
      const response = await fetch(`${backendUrl}/api/community/users/mentionable?query=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMentionSuggestions(data);
        setShowMentionSuggestions(data.length > 0);
      }
    } catch (error) {
      console.error('Error fetching mention suggestions:', error);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = value.substring(lastAtIndex + 1);
      const hasSpaceAfter = textAfterAt.includes(' ');
      if (!hasSpaceAfter && textAfterAt.length > 0) {
        handleMentionSearch(textAfterAt);
      } else if (textAfterAt.length === 0) {
        handleMentionSearch('');
      } else {
        setShowMentionSuggestions(false);
      }
    } else {
      setShowMentionSuggestions(false);
    }
  };

  const insertMention = (user) => {
    const lastAtIndex = newMessage.lastIndexOf('@');
    const beforeAt = newMessage.substring(0, lastAtIndex);
    const newText = `${beforeAt}@${user.name} `;
    setNewMessage(newText);
    setSelectedMentions([...selectedMentions, user.id]);
    setShowMentionSuggestions(false);
    inputRef.current?.focus();
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChannel) return;

    try {
      const response = await fetch(`${backendUrl}/api/community/channels/${activeChannel.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message_text: newMessage,
          attachments: [],
          parent_message_id: replyingTo?.id || null,
          mentions: selectedMentions
        })
      });

      if (response.ok) {
        const message = await response.json();
        if (replyingTo) {
          setThreadMessages(prev => ({
            ...prev,
            [replyingTo.id]: [...(prev[replyingTo.id] || []), message]
          }));
          setReplyingTo(null);
        } else {
          setMessages([...messages, message]);
        }
        setNewMessage('');
        setSelectedMentions([]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const pinMessage = async (messageId) => {
    try {
      const response = await fetch(`${backendUrl}/api/community/channels/${activeChannel.id}/messages/${messageId}/pin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPinnedMessages(activeChannel.id);
        fetchMessages(activeChannel.id);
      }
    } catch (error) {
      console.error('Error pinning message:', error);
    }
  };

  const unpinMessage = async (messageId) => {
    try {
      const response = await fetch(`${backendUrl}/api/community/channels/${activeChannel.id}/messages/${messageId}/pin`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPinnedMessages(activeChannel.id);
        fetchMessages(activeChannel.id);
      }
    } catch (error) {
      console.error('Error unpinning message:', error);
    }
  };

  const searchMessages = async () => {
    if (!searchQuery.trim()) return;
    try {
      const response = await fetch(`${backendUrl}/api/community/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Error searching:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderMessageText = (text, mentions = []) => {
    if (!mentions || mentions.length === 0) return text;
    
    let result = text;
    mentions.forEach(mention => {
      const mentionText = `@${mention.user_name}`;
      result = result.replace(
        new RegExp(mentionText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
        `<span class="bg-purple-100 text-purple-700 px-1 rounded font-medium">${mentionText}</span>`
      );
    });
    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };

  const handleChannelSelect = (channel) => {
    setActiveChannel(channel);
    setShowSidebar(false); // Close sidebar on mobile after selection
  };

  const canPinMessages = userRole === 'admin' || userRole === 'mentor';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading community...</p>
        </div>
      </div>
    );
  }

  // Show error message if there's one
  if (errorMessage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Community Error</h2>
          <p className="text-red-600 mb-4 p-3 bg-red-50 rounded-lg">{errorMessage}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (accessLevel && !accessLevel.access.community) {
    return <AccessGate feature="community" />;
  }

  return (
    <div className="flex h-screen bg-white pt-16 relative">
      {/* Mobile Overlay */}
      {showSidebar && (
        <div 
          className="fixed inset-0 bg-black/50 z-[60] md:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Left Sidebar - Channels */}
      <div 
        ref={sidebarRef}
        className={`
          fixed md:relative inset-y-0 left-0 z-[70] md:z-0
          w-72 md:w-64 bg-gray-900 text-white flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${showSidebar ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          pt-16 md:pt-0
        `}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">OA Community</h2>
            <p className="text-xs text-gray-400 mt-1">Academy Platform</p>
          </div>
          <button 
            onClick={() => setShowSidebar(false)}
            className="md:hidden p-2 hover:bg-gray-800 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Channels List */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-3 py-4">
            <div className="flex items-center justify-between px-2 mb-3">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Channels</h3>
              <span className="text-xs text-gray-500">{channels.length}</span>
            </div>
            
            <div className="space-y-1">
              {channels.map((channel) => (
                <button
                  key={channel.id}
                  onClick={() => handleChannelSelect(channel)}
                  className={`w-full flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    activeChannel?.id === channel.id 
                      ? 'bg-purple-600 hover:bg-purple-700 shadow-lg' 
                      : 'hover:bg-gray-800'
                  }`}
                >
                  <span className="mr-2.5 text-lg">{channel.emoji || '#'}</span>
                  <div className="flex-1 text-left">
                    <span className="text-sm font-medium">{channel.name}</span>
                    {channel.description && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">{channel.description}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* User Profile Footer */}
        <div className="p-3 border-t border-gray-800 bg-gray-900/50">
          <div className="flex items-center p-2 rounded-lg hover:bg-gray-800 transition-colors">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-sm font-bold shadow-lg">
              {user?.profile?.first_name?.[0] || user?.first_name?.[0] || 'U'}
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user?.profile?.first_name || user?.first_name || 'User'}
              </p>
              <div className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
                <span className="text-xs text-gray-400">Online</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Channel Header */}
        <div className="h-14 md:h-16 border-b border-gray-200 px-3 md:px-4 flex items-center justify-between bg-white shadow-sm relative z-10">
          <div className="flex items-center min-w-0 flex-1">
            {/* Mobile Menu Button */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowSidebar(true);
              }}
              className="md:hidden p-2 mr-2 hover:bg-gray-100 rounded-lg active:bg-gray-200 transition-colors touch-manipulation"
              aria-label="Open channel menu"
              data-testid="community-hamburger"
            >
              <Menu className="w-5 h-5 text-gray-700 pointer-events-none" />
            </button>
            
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center mr-2 flex-shrink-0">
                <Hash className="w-4 h-4 text-purple-600" />
              </div>
              <div className="min-w-0">
                <h2 className="font-semibold text-gray-900 truncate">
                  {activeChannel?.name || 'Select Channel'}
                </h2>
                <p className="text-xs text-gray-500 truncate hidden sm:block">
                  {activeChannel?.description || 'Choose a channel to start chatting'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-1 flex-shrink-0">
            {pinnedMessages.length > 0 && (
              <button 
                onClick={() => setShowPinnedPanel(!showPinnedPanel)}
                className="p-2 hover:bg-gray-100 rounded-lg flex items-center gap-1 text-gray-600"
              >
                <Pin className="w-4 h-4" />
                <span className="text-xs font-medium hidden sm:inline">{pinnedMessages.length}</span>
              </button>
            )}
            <button 
              onClick={() => setShowSearch(!showSearch)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <Search className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Search Panel */}
        {showSearch && (
          <div className="border-b border-gray-200 p-3 md:p-4 bg-gray-50">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchMessages()}
                placeholder="Search messages..."
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                onClick={searchMessages}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
              >
                Search
              </button>
              <button
                onClick={() => { setShowSearch(false); setSearchResults([]); setSearchQuery(''); }}
                className="p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="mt-3 max-h-48 overflow-y-auto space-y-2">
                {searchResults.map((result) => (
                  <div key={result.id} className="p-2.5 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{result.user_name}</span>
                      <span className="text-xs text-gray-500">#{result.channel_name}</span>
                    </div>
                    <p className="text-sm text-gray-700 line-clamp-2">{result.message_text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pinned Messages Panel */}
        {showPinnedPanel && pinnedMessages.length > 0 && (
          <div className="border-b border-gray-200 p-3 md:p-4 bg-amber-50">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm">
                <Pin className="w-4 h-4 text-amber-600" /> Pinned Messages
              </h3>
              <button onClick={() => setShowPinnedPanel(false)} className="p-1 hover:bg-amber-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {pinnedMessages.map((msg) => (
                <div key={msg.id} className="p-2 bg-white rounded-lg border border-amber-200 flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-sm">{msg.user_name}: </span>
                    <span className="text-sm text-gray-700">{msg.message_text}</span>
                  </div>
                  {canPinMessages && (
                    <button onClick={() => unpinMessage(msg.id)} className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto bg-white">
          {!activeChannel ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Welcome to Community</h3>
              <p className="text-gray-600 text-sm">Select a channel from the menu to start chatting</p>
              <button 
                onClick={() => setShowSidebar(true)}
                className="mt-4 md:hidden px-4 py-2 bg-purple-600 text-white rounded-lg font-medium text-sm"
              >
                Open Channels
              </button>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Hash className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Welcome to #{activeChannel.name}
              </h3>
              <p className="text-gray-600 max-w-sm text-sm">
                {activeChannel.description || 'This is the start of this channel'}. Be the first to send a message!
              </p>
            </div>
          ) : (
            <div className="p-3 md:p-4 space-y-3">
              {messages.filter(m => !m.parent_message_id).map((message) => (
                <div key={message.id} className="group">
                  <div className="flex hover:bg-gray-50 p-2 md:px-3 md:py-2 rounded-lg transition-colors">
                    <div className="flex-shrink-0 mr-2.5 md:mr-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-xs md:text-sm font-bold">
                        {message.user_name?.split(' ').map(n => n[0]).join('').substring(0, 2) || 'U'}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline flex-wrap gap-x-2">
                        <span className="font-semibold text-gray-900 text-sm">{message.user_name}</span>
                        <span className="text-xs text-gray-500">{formatTime(message.created_at)}</span>
                        {message.is_edited && <span className="text-xs text-gray-400">(edited)</span>}
                        {message.is_pinned && <Pin className="w-3 h-3 text-amber-500 inline" />}
                      </div>
                      <p className="text-gray-800 text-sm break-words mt-0.5">
                        {renderMessageText(message.message_text, message.mentions)}
                      </p>
                      
                      {/* Thread replies indicator */}
                      {message.reply_count > 0 && (
                        <button
                          onClick={() => fetchThreadReplies(message.id)}
                          className="mt-2 text-purple-600 text-xs flex items-center gap-1 hover:underline"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
                        </button>
                      )}
                      
                      {/* Thread messages */}
                      {threadMessages[message.id] && (
                        <div className="mt-2 ml-2 md:ml-4 border-l-2 border-purple-200 pl-2 md:pl-3 space-y-2">
                          {threadMessages[message.id].map((reply) => (
                            <div key={reply.id} className="bg-gray-50 p-2 rounded-lg">
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="font-medium text-sm">{reply.user_name}</span>
                                <span className="text-xs text-gray-500">{formatTime(reply.created_at)}</span>
                              </div>
                              <p className="text-sm text-gray-700 mt-0.5">{renderMessageText(reply.message_text, reply.mentions)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Message Actions - visible on hover for desktop, always visible button on mobile */}
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-start space-x-0.5 md:space-x-1 flex-shrink-0 ml-2">
                      <button 
                        onClick={() => setReplyingTo(message)}
                        className="p-1.5 hover:bg-gray-200 rounded text-gray-500" 
                        title="Reply"
                      >
                        <Reply className="w-4 h-4" />
                      </button>
                      {canPinMessages && !message.is_pinned && (
                        <button 
                          onClick={() => pinMessage(message.id)}
                          className="p-1.5 hover:bg-gray-200 rounded text-gray-500" 
                          title="Pin"
                        >
                          <Pin className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Reply indicator */}
        {replyingTo && (
          <div className="px-3 md:px-4 py-2 bg-purple-50 border-t border-purple-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm min-w-0">
              <Reply className="w-4 h-4 text-purple-600 flex-shrink-0" />
              <span className="truncate">Replying to <strong>{replyingTo.user_name}</strong></span>
            </div>
            <button onClick={() => setReplyingTo(null)} className="text-gray-500 hover:text-gray-700 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Message Input */}
        <div className="p-3 md:p-4 border-t border-gray-200 bg-white relative">
          {/* Mention Suggestions */}
          {showMentionSuggestions && mentionSuggestions.length > 0 && (
            <div className="absolute bottom-full left-3 right-3 md:left-4 md:right-4 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto z-10">
              {mentionSuggestions.map((user) => (
                <button
                  key={user.id}
                  onClick={() => insertMention(user)}
                  className="w-full px-3 py-2 text-left hover:bg-purple-50 flex items-center gap-2"
                >
                  <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xs font-semibold flex-shrink-0">
                    {user.name?.[0] || 'U'}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          <form onSubmit={sendMessage}>
            <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:border-purple-500 focus-within:ring-2 focus-within:ring-purple-100 bg-white shadow-sm">
              <textarea
                ref={inputRef}
                value={newMessage}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(e);
                  }
                }}
                placeholder={
                  !activeChannel 
                    ? 'Select a channel to start messaging' 
                    : replyingTo 
                      ? `Reply to ${replyingTo.user_name}...` 
                      : `Message #${activeChannel.name}`
                }
                disabled={!activeChannel}
                className="w-full px-3 py-2.5 md:px-4 md:py-3 focus:outline-none resize-none text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                rows="1"
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
              <div className="px-2 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center space-x-1">
                  <button 
                    type="button" 
                    onClick={() => { setNewMessage(newMessage + '@'); inputRef.current?.focus(); }}
                    className="p-1.5 md:p-2 hover:bg-gray-200 rounded-lg text-gray-500"
                    title="Mention someone"
                    disabled={!activeChannel}
                  >
                    <AtSign className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-1.5 md:p-2 hover:bg-gray-200 rounded-lg text-gray-500"
                    disabled={!activeChannel}
                  >
                    <Smile className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={!newMessage.trim() || !activeChannel}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
                >
                  <Send className="w-4 h-4" />
                  <span className="hidden sm:inline">Send</span>
                </button>
              </div>
            </div>
          </form>

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div 
              ref={emojiPickerRef}
              className="absolute bottom-20 left-3 right-3 md:left-4 md:right-auto z-50 shadow-xl rounded-xl bg-white border border-gray-200 p-3 md:w-80 max-h-56 overflow-y-auto"
            >
              <div className="grid grid-cols-8 gap-1">
                {['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊', '😇', '🥰', '😍', '🤩', '😘', '😋', '👍', '👎', '👏', '🙌', '🤝', '🙏', '💪', '❤️', '🧡', '💛', '💚', '💙', '💜', '🔥', '⭐', '✨'].map((emoji, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => { setNewMessage(newMessage + emoji); setShowEmojiPicker(false); }}
                    className="text-xl md:text-2xl hover:bg-gray-100 p-1.5 rounded-lg transition-colors"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityPage;
